import React from 'react';
import {
  Box,
  Container,
  Typography,
  Paper,
  Divider,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  Link,
  Alert,
} from '@mui/material';
import {
  Gavel as GavelIcon,
  CheckCircle as CheckIcon,
  Warning as WarningIcon,
  Email as EmailIcon,
} from '@mui/icons-material';

// ============================================================================
// TERMS OF SERVICE PAGE
// Comprehensive terms of service for Check Set Go platform
// ============================================================================

const TermsOfService: React.FC = () => {
  const lastUpdated = 'December 8, 2025';
  const effectiveDate = 'December 8, 2025';
  const companyName = 'Centre Of Business';
  const appName = 'Check Set Go';
  const contactEmail = 'support@checksetgo.com';
  const supportEmail = 'support@checksetgo.com';

  // Section component for consistent styling
  const Section: React.FC<{ title: string; children: React.ReactNode }> = ({ title, children }) => (
    <Box sx={{ mb: 4 }}>
      <Typography variant="h5" sx={{ mb: 2, color: 'primary.main', fontWeight: 600 }}>
        {title}
      </Typography>
      {children}
    </Box>
  );

  // Subsection component
  const SubSection: React.FC<{ title: string; children: React.ReactNode }> = ({ title, children }) => (
    <Box sx={{ mb: 3 }}>
      <Typography variant="h6" sx={{ mb: 1.5, fontWeight: 600 }}>
        {title}
      </Typography>
      {children}
    </Box>
  );

  return (
    <Container maxWidth="md" sx={{ py: 4 }}>
      <Paper elevation={0} sx={{ p: { xs: 3, md: 6 }, borderRadius: 3, border: '1px solid #E0E0E0' }}>
        {/* Header */}
        <Box sx={{ textAlign: 'center', mb: 4 }}>
          <GavelIcon sx={{ fontSize: 48, color: 'primary.main', mb: 2 }} />
          <Typography variant="h3" sx={{ fontWeight: 700, mb: 1 }}>
            Terms of Service
            </Typography>
            <Typography variant="body2" color="text.secondary">
              Last updated: {lastUpdated}
            </Typography>
            <Typography variant="body2" color="text.secondary">
              Effective date: {effectiveDate}
            </Typography>
          </Box>

          <Alert severity="info" sx={{ mb: 4 }}>
            Please read these Terms of Service carefully before using {appName}. By accessing or using 
            our Service, you agree to be bound by these terms.
          </Alert>

          <Divider sx={{ mb: 4 }} />

          {/* Acceptance of Terms */}
          <Section title="1. Acceptance of Terms">
            <Typography paragraph>
              These Terms of Service ("Terms") govern your access to and use of the {appName} platform 
              ("Service") operated by {companyName} ABN [Your ABN] ("we", "us", or "our").
            </Typography>
            <Typography paragraph>
              By creating an account, accessing, or using the Service, you agree to be bound by these 
              Terms and our Privacy Policy. If you do not agree to these Terms, you may not access or 
              use the Service.
            </Typography>
            <Typography paragraph>
              If you are using the Service on behalf of an organization, you represent and warrant that 
              you have the authority to bind that organization to these Terms.
            </Typography>
          </Section>

          {/* Description of Service */}
          <Section title="2. Description of Service">
            <Typography paragraph>
              {appName} is a multi-tenant SaaS platform that enables consultants to:
            </Typography>
            <List dense>
              <ListItem>
                <ListItemIcon><CheckIcon color="primary" /></ListItemIcon>
                <ListItemText primary="Create and manage audit templates using AI-powered tools" />
              </ListItem>
              <ListItem>
                <ListItemIcon><CheckIcon color="primary" /></ListItemIcon>
                <ListItemText primary="Conduct business audits for client companies" />
              </ListItem>
              <ListItem>
                <ListItemIcon><CheckIcon color="primary" /></ListItemIcon>
                <ListItemText primary="Generate AI-powered analysis and recommendations" />
              </ListItem>
              <ListItem>
                <ListItemIcon><CheckIcon color="primary" /></ListItemIcon>
                <ListItemText primary="Track action items and sub-tasks arising from audits" />
              </ListItem>
              <ListItem>
                <ListItemIcon><CheckIcon color="primary" /></ListItemIcon>
                <ListItemText primary="Collaborate with team members and clients" />
              </ListItem>
            </List>
          </Section>

          {/* User Accounts */}
          <Section title="3. User Accounts">
            <SubSection title="3.1 Account Registration">
              <Typography paragraph>
                To use the Service, you must create an account by providing accurate and complete 
                information. You agree to:
              </Typography>
              <List dense>
                <ListItem>
                  <ListItemIcon><CheckIcon color="primary" /></ListItemIcon>
                  <ListItemText primary="Provide accurate, current, and complete information" />
                </ListItem>
                <ListItem>
                  <ListItemIcon><CheckIcon color="primary" /></ListItemIcon>
                  <ListItemText primary="Maintain and promptly update your account information" />
                </ListItem>
                <ListItem>
                  <ListItemIcon><CheckIcon color="primary" /></ListItemIcon>
                  <ListItemText primary="Keep your password secure and confidential" />
                </ListItem>
                <ListItem>
                  <ListItemIcon><CheckIcon color="primary" /></ListItemIcon>
                  <ListItemText primary="Notify us immediately of any unauthorized access" />
                </ListItem>
              </List>
            </SubSection>

            <SubSection title="3.2 Account Types">
              <Typography paragraph>
                The Service supports different account types with varying permissions:
              </Typography>
              <List dense>
                <ListItem>
                  <ListItemIcon><CheckIcon color="primary" /></ListItemIcon>
                  <ListItemText 
                    primary="Consultant Accounts" 
                    secondary="For consulting businesses managing client audits"
                  />
                </ListItem>
                <ListItem>
                  <ListItemIcon><CheckIcon color="primary" /></ListItemIcon>
                  <ListItemText 
                    primary="Team Member Accounts" 
                    secondary="For employees of consulting businesses"
                  />
                </ListItem>
                <ListItem>
                  <ListItemIcon><CheckIcon color="primary" /></ListItemIcon>
                  <ListItemText 
                    primary="Client Accounts" 
                    secondary="For client company users accessing their audit data"
                  />
                </ListItem>
              </List>
            </SubSection>

            <SubSection title="3.3 Account Termination">
              <Typography paragraph>
                You may terminate your account at any time through your account settings. We may 
                suspend or terminate your account if you:
              </Typography>
              <List dense>
                <ListItem>
                  <ListItemIcon><WarningIcon color="warning" /></ListItemIcon>
                  <ListItemText primary="Violate these Terms or any applicable laws" />
                </ListItem>
                <ListItem>
                  <ListItemIcon><WarningIcon color="warning" /></ListItemIcon>
                  <ListItemText primary="Engage in fraudulent or abusive conduct" />
                </ListItem>
                <ListItem>
                  <ListItemIcon><WarningIcon color="warning" /></ListItemIcon>
                  <ListItemText primary="Fail to pay applicable fees" />
                </ListItem>
                <ListItem>
                  <ListItemIcon><WarningIcon color="warning" /></ListItemIcon>
                  <ListItemText primary="Pose a security risk to the Service or other users" />
                </ListItem>
              </List>
            </SubSection>
          </Section>

          {/* Subscription and Billing */}
          <Section title="4. Subscription and Billing">
            <SubSection title="4.1 Subscription Plans">
              <Typography paragraph>
                The Service is offered through subscription plans. Current pricing and features are 
                available on our website. We reserve the right to modify pricing with 30 days' notice.
              </Typography>
            </SubSection>

            <SubSection title="4.2 Free Trial">
              <Typography paragraph>
                New accounts may receive a free trial period. During the trial, you will have access 
                to the Service features as specified. At the end of the trial, you must subscribe to 
                continue using the Service.
              </Typography>
            </SubSection>

            <SubSection title="4.3 Payment Terms">
              <List dense>
                <ListItem>
                  <ListItemIcon><CheckIcon color="primary" /></ListItemIcon>
                  <ListItemText primary="Subscriptions are billed in advance on a monthly or annual basis" />
                </ListItem>
                <ListItem>
                  <ListItemIcon><CheckIcon color="primary" /></ListItemIcon>
                  <ListItemText primary="Review credit packs are one-time purchases and do not expire" />
                </ListItem>
                <ListItem>
                  <ListItemIcon><CheckIcon color="primary" /></ListItemIcon>
                  <ListItemText primary="All payments are processed securely through Stripe" />
                </ListItem>
                <ListItem>
                  <ListItemIcon><CheckIcon color="primary" /></ListItemIcon>
                  <ListItemText primary="Prices are in Australian Dollars (AUD) unless otherwise stated" />
                </ListItem>
              </List>
            </SubSection>

            <SubSection title="4.4 Refunds">
              <Typography paragraph>
                Subscription fees are non-refundable except as required by Australian Consumer Law. 
                Unused audit credits remain valid and are not refundable. If you believe you are 
                entitled to a refund, please contact us at {supportEmail}.
              </Typography>
            </SubSection>

            <SubSection title="4.5 Cancellation">
              <Typography paragraph>
                You may cancel your subscription at any time. Upon cancellation:
              </Typography>
              <List dense>
                <ListItem>
                  <ListItemIcon><CheckIcon color="primary" /></ListItemIcon>
                  <ListItemText primary="You will retain access until the end of your billing period" />
                </ListItem>
                <ListItem>
                  <ListItemIcon><CheckIcon color="primary" /></ListItemIcon>
                  <ListItemText primary="Your data will be retained for 30 days after expiration" />
                </ListItem>
                <ListItem>
                  <ListItemIcon><CheckIcon color="primary" /></ListItemIcon>
                  <ListItemText primary="You can export your data before the retention period ends" />
                </ListItem>
                <ListItem>
                  <ListItemIcon><CheckIcon color="primary" /></ListItemIcon>
                  <ListItemText primary="Unused audit credits remain valid if you resubscribe" />
                </ListItem>
              </List>
            </SubSection>
          </Section>

          {/* Acceptable Use */}
          <Section title="5. Acceptable Use Policy">
            <SubSection title="5.1 Permitted Use">
              <Typography paragraph>
                You may use the Service only for lawful business purposes in accordance with these 
                Terms. You agree to use the Service in a manner consistent with all applicable laws 
                and regulations.
              </Typography>
            </SubSection>

            <SubSection title="5.2 Prohibited Activities">
              <Typography paragraph>
                You agree NOT to:
              </Typography>
              <List dense>
                <ListItem>
                  <ListItemIcon><WarningIcon color="error" /></ListItemIcon>
                  <ListItemText primary="Use the Service for any illegal purpose or in violation of any laws" />
                </ListItem>
                <ListItem>
                  <ListItemIcon><WarningIcon color="error" /></ListItemIcon>
                  <ListItemText primary="Attempt to gain unauthorized access to any part of the Service" />
                </ListItem>
                <ListItem>
                  <ListItemIcon><WarningIcon color="error" /></ListItemIcon>
                  <ListItemText primary="Interfere with or disrupt the Service or servers" />
                </ListItem>
                <ListItem>
                  <ListItemIcon><WarningIcon color="error" /></ListItemIcon>
                  <ListItemText primary="Upload malicious code, viruses, or harmful content" />
                </ListItem>
                <ListItem>
                  <ListItemIcon><WarningIcon color="error" /></ListItemIcon>
                  <ListItemText primary="Scrape, harvest, or collect data from the Service without permission" />
                </ListItem>
                <ListItem>
                  <ListItemIcon><WarningIcon color="error" /></ListItemIcon>
                  <ListItemText primary="Impersonate any person or entity" />
                </ListItem>
                <ListItem>
                  <ListItemIcon><WarningIcon color="error" /></ListItemIcon>
                  <ListItemText primary="Share your account credentials with others" />
                </ListItem>
                <ListItem>
                  <ListItemIcon><WarningIcon color="error" /></ListItemIcon>
                  <ListItemText primary="Use the Service to send spam or unsolicited communications" />
                </ListItem>
                <ListItem>
                  <ListItemIcon><WarningIcon color="error" /></ListItemIcon>
                  <ListItemText primary="Reverse engineer, decompile, or disassemble the Service" />
                </ListItem>
              </List>
            </SubSection>

            <SubSection title="5.3 Content Standards">
              <Typography paragraph>
                Any content you upload or create using the Service must not:
              </Typography>
              <List dense>
                <ListItem>
                  <ListItemIcon><WarningIcon color="error" /></ListItemIcon>
                  <ListItemText primary="Infringe any intellectual property rights" />
                </ListItem>
                <ListItem>
                  <ListItemIcon><WarningIcon color="error" /></ListItemIcon>
                  <ListItemText primary="Contain defamatory, obscene, or offensive material" />
                </ListItem>
                <ListItem>
                  <ListItemIcon><WarningIcon color="error" /></ListItemIcon>
                  <ListItemText primary="Violate the privacy rights of any person" />
                </ListItem>
                <ListItem>
                  <ListItemIcon><WarningIcon color="error" /></ListItemIcon>
                  <ListItemText primary="Contain false or misleading information" />
                </ListItem>
              </List>
            </SubSection>
          </Section>

          {/* Intellectual Property */}
          <Section title="6. Intellectual Property">
            <SubSection title="6.1 Our Intellectual Property">
              <Typography paragraph>
                The Service, including its original content, features, and functionality, is owned by 
                {companyName} and is protected by international copyright, trademark, patent, trade 
                secret, and other intellectual property laws.
              </Typography>
              <Typography paragraph>
                The {appName} name, logo, and all related names, logos, product and service names, 
                designs, and slogans are trademarks of {companyName}. You may not use these marks 
                without our prior written permission.
              </Typography>
            </SubSection>

            <SubSection title="6.2 Your Content">
              <Typography paragraph>
                You retain ownership of all content you create or upload to the Service ("Your Content"). 
                By using the Service, you grant us a worldwide, non-exclusive, royalty-free license to:
              </Typography>
              <List dense>
                <ListItem>
                  <ListItemIcon><CheckIcon color="primary" /></ListItemIcon>
                  <ListItemText primary="Store, process, and display Your Content to provide the Service" />
                </ListItem>
                <ListItem>
                  <ListItemIcon><CheckIcon color="primary" /></ListItemIcon>
                  <ListItemText primary="Create backups and maintain Service integrity" />
                </ListItem>
                <ListItem>
                  <ListItemIcon><CheckIcon color="primary" /></ListItemIcon>
                  <ListItemText primary="Generate anonymized, aggregated analytics" />
                </ListItem>
              </List>
              <Typography paragraph>
                This license terminates when you delete Your Content or your account.
              </Typography>
            </SubSection>

            <SubSection title="6.3 Audit Templates">
              <Typography paragraph>
                Audit templates you create remain your intellectual property. If you choose to share 
                templates with the community:
              </Typography>
              <List dense>
                <ListItem>
                  <ListItemIcon><CheckIcon color="primary" /></ListItemIcon>
                  <ListItemText primary="You grant other users a license to use and adapt the template" />
                </ListItem>
                <ListItem>
                  <ListItemIcon><CheckIcon color="primary" /></ListItemIcon>
                  <ListItemText primary="You will be credited as the template creator" />
                </ListItem>
                <ListItem>
                  <ListItemIcon><CheckIcon color="primary" /></ListItemIcon>
                  <ListItemText primary="You can withdraw sharing at any time (existing copies remain)" />
                </ListItem>
              </List>
            </SubSection>

            <SubSection title="6.4 AI-Generated Content">
              <Typography paragraph>
                Content generated by AI features (such as audit analysis, templates, and sub-tasks) is 
                provided as a tool to assist you. You are responsible for reviewing and verifying 
                AI-generated content before use. We do not claim ownership of AI-generated content 
                created for your use.
              </Typography>
            </SubSection>
          </Section>

          {/* Data and Privacy */}
          <Section title="7. Data and Privacy">
            <Typography paragraph>
              Your privacy is important to us. Our collection and use of personal information is 
              governed by our <Link href="/privacy-policy">Privacy Policy</Link>, which is incorporated 
              into these Terms by reference.
            </Typography>
            <Typography paragraph>
              By using the Service, you consent to the collection and use of your information as 
              described in our Privacy Policy.
            </Typography>
          </Section>

          {/* Disclaimers */}
          <Section title="8. Disclaimers">
            <Alert severity="warning" sx={{ mb: 2 }}>
              Please read this section carefully as it limits our liability.
            </Alert>

            <SubSection title="8.1 Service Provided 'As Is'">
              <Typography paragraph>
                THE SERVICE IS PROVIDED ON AN "AS IS" AND "AS AVAILABLE" BASIS WITHOUT WARRANTIES OF 
                ANY KIND, EITHER EXPRESS OR IMPLIED, INCLUDING BUT NOT LIMITED TO IMPLIED WARRANTIES 
                OF MERCHANTABILITY, FITNESS FOR A PARTICULAR PURPOSE, AND NON-INFRINGEMENT.
              </Typography>
            </SubSection>

            <SubSection title="8.2 No Professional Advice">
              <Typography paragraph>
                The Service, including AI-generated content, is intended to assist with business 
                auditing processes. It does not constitute professional, legal, financial, or 
                compliance advice. You should consult appropriate professionals before making 
                business decisions based on audit results.
              </Typography>
            </SubSection>

            <SubSection title="8.3 AI Limitations">
              <Typography paragraph>
                AI-generated content may contain errors, inaccuracies, or omissions. You are solely 
                responsible for reviewing, verifying, and validating any AI-generated content before 
                use. We do not guarantee the accuracy, completeness, or reliability of AI-generated 
                content.
              </Typography>
            </SubSection>

            <SubSection title="8.4 Third-Party Services">
              <Typography paragraph>
                The Service integrates with third-party services (such as Stripe for payments). We are 
                not responsible for the availability, accuracy, or content of third-party services.
              </Typography>
            </SubSection>
          </Section>

          {/* Limitation of Liability */}
          <Section title="9. Limitation of Liability">
            <Typography paragraph>
              TO THE MAXIMUM EXTENT PERMITTED BY APPLICABLE LAW:
            </Typography>
            <List dense>
              <ListItem>
                <ListItemIcon><WarningIcon color="warning" /></ListItemIcon>
                <ListItemText 
                  primary="No Consequential Damages" 
                  secondary="We shall not be liable for any indirect, incidental, special, consequential, or punitive damages"
                />
              </ListItem>
              <ListItem>
                <ListItemIcon><WarningIcon color="warning" /></ListItemIcon>
                <ListItemText 
                  primary="Liability Cap" 
                  secondary="Our total liability shall not exceed the amount you paid us in the 12 months preceding the claim"
                />
              </ListItem>
              <ListItem>
                <ListItemIcon><WarningIcon color="warning" /></ListItemIcon>
                <ListItemText 
                  primary="Data Loss" 
                  secondary="We are not liable for any loss of data, even if we have been advised of the possibility"
                />
              </ListItem>
            </List>
            <Typography paragraph sx={{ mt: 2 }}>
              Nothing in these Terms excludes or limits liability that cannot be excluded or limited 
              under applicable law, including under the Australian Consumer Law.
            </Typography>
          </Section>

          {/* Indemnification */}
          <Section title="10. Indemnification">
            <Typography paragraph>
              You agree to indemnify, defend, and hold harmless {companyName}, its officers, directors, 
              employees, agents, and affiliates from and against any claims, liabilities, damages, 
              losses, and expenses (including reasonable legal fees) arising out of or in any way 
              connected with:
            </Typography>
            <List dense>
              <ListItem>
                <ListItemIcon><CheckIcon color="primary" /></ListItemIcon>
                <ListItemText primary="Your access to or use of the Service" />
              </ListItem>
              <ListItem>
                <ListItemIcon><CheckIcon color="primary" /></ListItemIcon>
                <ListItemText primary="Your violation of these Terms" />
              </ListItem>
              <ListItem>
                <ListItemIcon><CheckIcon color="primary" /></ListItemIcon>
                <ListItemText primary="Your violation of any third-party rights" />
              </ListItem>
              <ListItem>
                <ListItemIcon><CheckIcon color="primary" /></ListItemIcon>
                <ListItemText primary="Your Content or any content you submit through the Service" />
              </ListItem>
            </List>
          </Section>

          {/* Dispute Resolution */}
          <Section title="11. Dispute Resolution">
            <SubSection title="11.1 Informal Resolution">
              <Typography paragraph>
                Before initiating any formal dispute resolution, you agree to first contact us at 
                {contactEmail} to attempt to resolve the dispute informally. We will try to resolve 
                any disputes within 30 days.
              </Typography>
            </SubSection>

            <SubSection title="11.2 Mediation">
              <Typography paragraph>
                If we cannot resolve a dispute informally, either party may refer the dispute to 
                mediation administered by the Australian Disputes Centre (ADC) in accordance with 
                ADC Mediation Rules.
              </Typography>
            </SubSection>

            <SubSection title="11.3 Jurisdiction">
              <Typography paragraph>
                If mediation is unsuccessful, any legal proceedings must be brought in the courts of 
                Queensland, Australia, and you consent to the exclusive jurisdiction of those courts.
              </Typography>
            </SubSection>
          </Section>

          {/* Governing Law */}
          <Section title="12. Governing Law">
            <Typography paragraph>
              These Terms shall be governed by and construed in accordance with the laws of Queensland, 
              Australia, without regard to its conflict of law provisions.
            </Typography>
            <Typography paragraph>
              Nothing in these Terms excludes, restricts, or modifies any consumer rights under the 
              Competition and Consumer Act 2010 (Cth) or any other applicable Australian consumer 
              protection legislation.
            </Typography>
          </Section>

          {/* Changes to Terms */}
          <Section title="13. Changes to Terms">
            <Typography paragraph>
              We reserve the right to modify these Terms at any time. We will provide notice of 
              material changes by:
            </Typography>
            <List dense>
              <ListItem>
                <ListItemIcon><CheckIcon color="primary" /></ListItemIcon>
                <ListItemText primary="Posting the updated Terms on our website" />
              </ListItem>
              <ListItem>
                <ListItemIcon><CheckIcon color="primary" /></ListItemIcon>
                <ListItemText primary="Updating the 'Last updated' date" />
              </ListItem>
              <ListItem>
                <ListItemIcon><CheckIcon color="primary" /></ListItemIcon>
                <ListItemText primary="Sending an email notification for significant changes" />
              </ListItem>
            </List>
            <Typography paragraph>
              Your continued use of the Service after changes become effective constitutes acceptance 
              of the revised Terms. If you do not agree to the revised Terms, you must stop using 
              the Service.
            </Typography>
          </Section>

          {/* General Provisions */}
          <Section title="14. General Provisions">
            <SubSection title="14.1 Entire Agreement">
              <Typography paragraph>
                These Terms, together with our Privacy Policy and any other agreements expressly 
                incorporated by reference, constitute the entire agreement between you and 
                {companyName} regarding the Service.
              </Typography>
            </SubSection>

            <SubSection title="14.2 Severability">
              <Typography paragraph>
                If any provision of these Terms is found to be unenforceable, the remaining provisions 
                will continue in full force and effect.
              </Typography>
            </SubSection>

            <SubSection title="14.3 Waiver">
              <Typography paragraph>
                Our failure to enforce any right or provision of these Terms will not be considered a 
                waiver of those rights.
              </Typography>
            </SubSection>

            <SubSection title="14.4 Assignment">
              <Typography paragraph>
                You may not assign or transfer these Terms without our prior written consent. We may 
                assign our rights and obligations under these Terms without restriction.
              </Typography>
            </SubSection>
          </Section>

          {/* Contact */}
          <Section title="15. Contact Us">
            <Typography paragraph>
              If you have any questions about these Terms, please contact us:
            </Typography>
            <Box sx={{ 
              p: 3, 
              bgcolor: 'grey.50', 
              borderRadius: 2, 
              border: '1px solid',
              borderColor: 'grey.200'
            }}>
              <Typography variant="h6" sx={{ mb: 2 }}>{companyName}</Typography>
              <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                <EmailIcon sx={{ mr: 1, color: 'primary.main' }} />
                <Typography>
                  Legal inquiries: <Link href={`mailto:${contactEmail}`}>{contactEmail}</Link>
                </Typography>
              </Box>
              <Box sx={{ display: 'flex', alignItems: 'center' }}>
                <EmailIcon sx={{ mr: 1, color: 'primary.main' }} />
                <Typography>
                  Support: <Link href={`mailto:${supportEmail}`}>{supportEmail}</Link>
                </Typography>
              </Box>
            </Box>
          </Section>

          <Divider sx={{ my: 4 }} />

          {/* Footer */}
          <Box sx={{ textAlign: 'center' }}>
            <Typography variant="body2" color="text.secondary">
              © {new Date().getFullYear()} {companyName}. All rights reserved.
            </Typography>
            <Box sx={{ mt: 2, display: 'flex', justifyContent: 'center', gap: 3 }}>
              <Link href="/privacy-policy" color="primary">Privacy Policy</Link>
              <Link href="/cookie-policy" color="primary">Cookie Policy</Link>
            </Box>
          </Box>
      </Paper>
    </Container>
  );
};

export default TermsOfService;

